import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import { useEffect, useState } from "react";
import { Box, Tab } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import TrailersTable from "../../components/Fleet/TrailersTable/TrailersTable";
import TrucksTable from "../../components/Fleet/TrucksTable/TrucksTable";
import TruckPopup from "../../components/Fleet/TruckPopup/TruckPopup";
import TrailerPopup from "../../components/Fleet/TrailerPopup/TrailerPopup";
import MaintenanceTable from "../../components/Fleet/MaintenanceTable/MaintenanceTable";
import "../../assets/styles/pages/Fleet/assetsPage.css";
import GenerateAllAssetRepairsButton from "../../components/Fleet/GeneratePdfButtons/GenerateAllAssetRepairsButton/GenerateAllAssetRepairsButton";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import * as signalR from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import UnassignedTrailersTable from "../../components/Fleet/UnassignedTrailersTable/UnassignedTrailersTable";
import type { MaintenanceStatus } from "../../data/FormColumns/Fleet/maintenanceTableColumns";

const AssetsPage = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const tabQuery = searchParams.get("tab");
  const maintenanceOperationQuery = searchParams.get("operationPerformed");
  const maintenanceStatusQueryRaw = searchParams.get("maintenanceStatus");
  const validMaintenanceStatuses: MaintenanceStatus[] = [
    "overdue",
    "overdueSoon",
  ];
  const maintenanceStatusQuery = validMaintenanceStatuses.includes(
    maintenanceStatusQueryRaw as MaintenanceStatus,
  )
    ? (maintenanceStatusQueryRaw as MaintenanceStatus)
    : null;
  const initialTab = tabQuery ? parseInt(tabQuery, 10) : 1;

  const [selectedTab, setSelectedTab] = useState<number>(initialTab);

  useEffect(() => {
    const truckConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/truckHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    truckConnection.on("TruckUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["trucksTable"] });
    });

    const trailerConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/trailerHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    trailerConnection.on("TrailerUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["trailersTable"] });
    });

    truckConnection.start();
    trailerConnection.start();

    return () => {
      truckConnection.stop();
      trailerConnection.stop();
    };
  }, [token, queryClient]);

  const handleTabChange = (event: any, newValue: number) => {
    setSelectedTab(newValue);
    navigate(`?tab=${newValue}`, { replace: true });
  };

  return (
    <Box className="assets-page">
      <Box className="popup-buttons">
        <TruckPopup />
        <TrailerPopup />
        <GenerateAllAssetRepairsButton
          token={token}
          incidentsRepairsOnly={null}
        />
      </Box>
      <Box sx={{ width: "100%", mt: 1 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} centered>
          <Tab label="Trailers" />
          <Tab label="Trucks" />
          <Tab label="Maintenance" />
          <Tab label="Unassigned Trailers" />
        </Tabs>
        {selectedTab === 0 && (
          <Box className="tables-container">
            <TrailersTable />
          </Box>
        )}
        {selectedTab === 1 && (
          <Box className="tables-container">
            <TrucksTable />
          </Box>
        )}
        {selectedTab === 2 && (
          <Box className="maintenance-table-container">
            <MaintenanceTable
              allowedOperations={
                maintenanceOperationQuery
                  ? [maintenanceOperationQuery]
                  : undefined
              }
              allowedStatuses={
                maintenanceStatusQuery ? [maintenanceStatusQuery] : undefined
              }
            />
          </Box>
        )}
        {selectedTab === 3 && (
          <Box className="tables-container">
            <UnassignedTrailersTable />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssetsPage;
